/**
 * RuleActionsSelect — per-group MULTI action picker for the Rules tab.
 *
 * A rule can combine several logical actions. Rotating out and adding are TWO
 * explicit actions: "Set to queue" takes the matched element off the tracker
 * and puts it BACK IN the queue line; "Eliminate" takes it off the tracker
 * for good (bank keeps it — revivable only via "Bring back to queue"); add
 * "Add new element" for the queue to backfill the freed slot with the next
 * highest-priority element of the matched layer. "Exclude" drives the weight
 * preview. "Pause capped offers" / "Reactivate capped offers" act on a path's
 * offers by their partner cap (MD-1147). Switch off is still "soon".
 * Without a queue (Tres Commas) the queue verbs are not offered: useOfferedRuleActions.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { FlowLayer, RuleAction } from '@trafficpulse/shared';

import { RuleActionItem, RuleActionsTrigger, actionDisabledReason } from './actions';
import { useOfferedRuleActions } from './actions/use-offered-rule-actions';
import { RULE_ACTIONS as ACTIONS } from './rule-action-meta';
import {
  QUEUE_LIFECYCLE_ACTIONS,
  isActionDisabled,
  resolveActionToggle,
} from './rule-action-compat';
import { RULE_LAYERS_COMING_SOON } from './rule-layers-launch';

/** Every action states its consequence — the picker is where this is learned. */
const HINTS: Partial<Record<RuleAction, string>> = {
  exclude: 'stays live, but the optimizer stops re-weighting it',
  weight: 'pins or boosts the matched share of traffic — set the numbers below',
  'set-to-queue': 'off the tracker, back into the queue line',
  'set-to-waiting': 'off the tracker, parked in Waiting',
  eliminate: 'off the tracker for good — the bank keeps it',
  pause: 'its offers at their partner cap leave the path — the whole path when none would stay',
  reactivate: 'paused offers / paths come back once their cap has room again',
};

const ELEMENT_LABEL: Record<FlowLayer, string> = {
  paths: 'new path',
  landers: 'new lander',
  offers: 'new offer',
};

export interface RuleActionsSelectProps {
  value: RuleAction[];
  appliesTo: ReadonlyArray<FlowLayer>;
  onChange: (actions: RuleAction[]) => void;
}

const RuleActionsSelect: FC<RuleActionsSelectProps> = ({ value, appliesTo, onChange }) => {
  // A new group starts with no action on purpose, so the empty state must read
  // as "you still have to choose", not as a configured setting.
  const chosen = ACTIONS.filter((a) => value.includes(a.value));
  const elementHint = appliesTo.length
    ? appliesTo.map((l) => ELEMENT_LABEL[l]).join(' / ')
    : 'the matched layer';
  // Paths-focused launch: the queue-system actions are gated for landers/offers
  // (the queue ignores them); a rule on those layers can only Exclude.
  const targetsGatedLayer = appliesTo.some((l) => RULE_LAYERS_COMING_SOON.includes(l));
  const { offered, staleNote } = useOfferedRuleActions(value);

  /** Queue actions stay SELECTABLE — paths execute them; landers/offers are
   *  ignored by the queue for now. Note it, don't block: a mixed group still
   *  queues its paths. */
  const hintFor = (action: RuleAction, disabled: boolean): string | undefined => {
    const soon = ACTIONS.find((a) => a.value === action)?.soon;
    if (disabled && !soon) return actionDisabledReason(value, action);
    if (targetsGatedLayer && !disabled && QUEUE_LIFECYCLE_ACTIONS.includes(action))
      return 'paths only · landers/offers coming soon';
    if (action === 'add-element') return `swap in a ${elementHint} from the queue`;
    return HINTS[action];
  };

  // Enforce the logical model: exactly one of exclude / set-to-queue / eliminate,
  // and add-element only alongside a rotation verb (see rule-action-meta).
  // A stale queue verb (no queue on this tracker) can only be unticked.
  const toggle = (action: RuleAction, checked: boolean) => {
    if (!checked || !staleNote(action)) onChange(resolveActionToggle(value, action, checked));
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <RuleActionsTrigger chosen={chosen} summary={chosen.map((a) => a.label).join(' + ')} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-[100] min-w-[17rem] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {offered.map((a) => {
            const stale = staleNote(a.value);
            const disabled = !stale && (Boolean(a.soon) || isActionDisabled(value, a.value));
            return (
              <RuleActionItem
                key={a.value}
                action={a}
                checked={value.includes(a.value)}
                disabled={disabled}
                hint={stale ?? hintFor(a.value, disabled)}
                onToggle={(checked) => toggle(a.value, checked)}
              />
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default RuleActionsSelect;
